import React from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { admin_cse } from "@/config/roles";
import { Button2 } from "@/ui2/atoms/Button";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { isAuthorized } from "@/redux/actions/auth";
import { showAlertWithAction } from "@/redux/actions/alerts";

import { filterNotesByNoteType } from "./(filters)/helpers/filter-notes";
import { NewNoteModal } from "./(filters)/NewNoteModal";
import { NoteFilters } from "./(filters)/NoteFilters";
import { NoteList } from "./(list)/NoteList";
import { NoteListSkeletons } from "./(list)/components/NoteListSkeletons";
import { sortNotesBySessionDate } from "./(filters)/helpers/sort-notes";
import { useNotesFilter } from "./hooks/useNotesFilter";
import { useNotesSelection } from "./hooks/useNotesSelection";
import { useNotesSummary } from "./api/queries/useNotesSummary";

const selectNotes = (data) => filterNotesByNoteType(sortNotesBySessionDate(data));

export function MemberProfileNotes() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { patientId } = useParams();

  const { data: notes, isLoading, isSuccess } = useNotesSummary({ patientId, select: selectNotes });

  const isAuthorizedToCreateNotes =
    isAuthorized("provider:behavioral") ||
    isAuthorized("provider:prescribe") ||
    isAuthorized(admin_cse) ||
    isAuthorized("provider:peer") ||
    isAuthorized("provider:case_manager");

  const isAuthorizedToExportNotes = isAuthorized(admin_cse);

  const {
    clearAll,
    dateEnd,
    dateStart,
    filteredNotes,
    isDirty,
    search,
    setDateEnd,
    setDateStart,
    setNoteType,
    setSearch,
  } = useNotesFilter(notes);

  const { isAllSelected, toggleAll, toggleNote, visibleSelectedIds } =
    useNotesSelection(filteredNotes);

  const exportNotes = () => {
    if (visibleSelectedIds.size > 50) {
      dispatch(
        showAlertWithAction(
          "Too many notes selected for export. Please reduce the selection and try again (max: 50).",
        ),
      );

      return;
    }

    history.push(
      `/app/patient/${patientId}/export_notes?noteIds=${[...visibleSelectedIds].join(",")}`,
    );
  };

  return (
    <>
      <DashboardCard2
        Actions={
          <div className="tw:flex tw:gap-5">
            <Button2 isDisabled={!isDirty} onPress={clearAll} variant="text">
              Clear Filters
            </Button2>

            {isAuthorizedToExportNotes && (
              <Button2
                isDisabled={visibleSelectedIds.size === 0}
                onPress={exportNotes}
                variant="secondary"
              >
                Export
              </Button2>
            )}

            <NewNoteModal
              isAuthorizedToCreate={isAuthorizedToCreateNotes}
              sessionCount={notes?.length}
            />
          </div>
        }
        classNameHeader="tw:border-b-0 tw:md:mb-0! tw:md:pb-0! tw:items-start"
        rawChildren
        title="Filter Notes"
      >
        <NoteFilters
          dateEnd={dateEnd}
          dateStart={dateStart}
          isAllSelected={isAllSelected}
          isAuthorizedToExportNotes={isAuthorizedToExportNotes}
          onDateEndChange={setDateEnd}
          onDateStartChange={setDateStart}
          onNoteTypeChange={setNoteType}
          onSearchChange={setSearch}
          onToggleAll={toggleAll}
          search={search}
        />
      </DashboardCard2>

      {isLoading && <NoteListSkeletons />}
      {isSuccess && (
        <NoteList
          notes={filteredNotes}
          onToggleNote={toggleNote}
          onViewNote={(note) => {
            history.push(`/app/patient/${patientId}/note/${note.id}/v2`);
          }}
          selectedNoteIds={visibleSelectedIds}
        />
      )}
    </>
  );
}
